import React, { useState, useEffect } from 'react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';
import { uploadAPI } from '../services/api';
import { API_CONFIG } from '../config/api';
import toast from 'react-hot-toast';

const ImageUpload = ({ 
  value, 
  onChange, 
  currentImage, // For backward compatibility
  onImageChange, // For backward compatibility
  label, 
  placeholder = "Click to upload image",
  accept = "image/*,.pdf",
  maxSize = 10 * 1024 * 1024, // 10MB
  className = "",
  uploadType = "image" // "image" or "document"
}) => {
  // Handle both prop naming conventions for backward compatibility
  const imageValue = value || currentImage;
  const imageOnChange = onChange || onImageChange;
  
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(imageValue);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    if (!file || file.size > maxSize) {
      toast.error(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setUploading(true);
    try {
      console.log('Uploading file:', file.name, 'Type:', uploadType, 'Size:', file.size);
      
      let response;
      if (uploadType === "document") {
        response = await uploadAPI.uploadDocument(file);
      } else {
        response = await uploadAPI.uploadImage(file);
      }

      console.log('Upload response:', response);

      if (response.success) {
        const filePath = response.data.path;
        console.log('File uploaded to:', filePath);
        setPreview(filePath);
        if (imageOnChange) {
          imageOnChange(filePath);
        }
        toast.success('File uploaded successfully');
      } else {
        toast.error(response.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (file) => {
    if (file && file.size <= maxSize) {
      // Show preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
      
      // Upload file to server
      uploadFile(file);
    } else {
      toast.error(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setPreview(null);
    if (imageOnChange) {
      imageOnChange(''); // Send empty string instead of null
    }
  };

  // Update preview when value changes
  useEffect(() => {
    setPreview(imageValue);
  }, [imageValue]);

  // Helper function to get the full file URL
  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    
    // If it's already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // If it's a data URL, return as is
    if (filePath.startsWith('data:')) {
      return filePath;
    }
    
    // Construct the full URL using the API base URL
    // Remove '/api' from the base URL since files are served from the root
    const baseUrl = API_CONFIG.ASSETS_URL;
    const fullUrl = `${baseUrl}/${filePath}`;
    return fullUrl;
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {preview ? (
          <div className="relative">
            {preview.startsWith('data:') || preview.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <img
                src={getFileUrl(preview)}
                alt="Preview"
                className="max-w-full max-h-48 mx-auto rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg">
                <FiImage className="w-12 h-12 text-gray-400" />
                <div className="ml-3 text-left">
                  <p className="text-sm font-medium text-gray-900">Document uploaded</p>
                  <p className="text-xs text-gray-500">{preview.split('/').pop()}</p>
                  <a 
                    href={getFileUrl(preview)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-700 mt-1 inline-block"
                    onClick={(e) => {
                      console.log('Opening file:', preview);
                      console.log('Full URL:', getFileUrl(preview));
                    }}
                  >
                    View Document
                  </a>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        ) : (
                      <div>
              <FiImage className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-2">
                <label className={`cursor-pointer ${uploading ? 'pointer-events-none opacity-50' : ''}`}>
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    {uploading ? 'Uploading...' : placeholder}
                  </span>
                  <input
                    type="file"
                    accept={accept}
                    onChange={handleChange}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Images, PDFs, Documents up to {maxSize / (1024 * 1024)}MB
                </p>
              </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload; 